import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import PageBackButton from '../../components/PageBackButton';

type AdminOverviewResponse = {
  users: {
    total: number;
    clients: number;
    professionals: number;
    new_7d: number;
    new_30d: number;
  };
  tasks: {
    total: number;
    open: number;
    in_progress: number;
    completed: number;
    new_7d: number;
    new_30d: number;
  };
  bids: {
    total: number;
    pending: number;
    accepted: number;
    new_7d: number;
  };
  messages: {
    unread_total: number;
    new_7d: number;
  };
  recent_users: Array<{
    id: number;
    email: string;
    full_name?: string;
    user_type: string;
    is_active: boolean;
    created_at?: string;
  }>;
  recent_tasks: Array<{
    id: number;
    title: string;
    status: string;
    user_id: number;
    contractor_id?: number;
    created_at?: string;
  }>;
};

type AdminUsersResponse = {
  total: number;
  limit: number;
  offset: number;
  items: Array<{
    id: number;
    email: string;
    username?: string;
    full_name?: string;
    user_type: string;
    is_active: boolean;
    is_superuser: boolean;
    created_at?: string;
    can_manage: boolean;
  }>;
};

type AdminAuditLogsResponse = {
  total: number;
  limit: number;
  offset: number;
  items: Array<{
    id: number;
    admin_user_id?: number;
    admin_user_email?: string;
    action: string;
    entity_type: string;
    entity_id?: number;
    target_user_id?: number;
    details?: string;
    created_at?: string;
  }>;
};

type AdminReportsResponse = {
  total: number;
  limit: number;
  offset: number;
  endpoint_unavailable?: boolean;
  items: Array<{
    id: number;
    reporter_user_id?: number;
    reporter_user_email?: string;
    target_type: 'task' | 'profile' | 'message';
    target_id: number;
    reason: string;
    description?: string;
    status: 'open' | 'in_review' | 'resolved' | 'rejected';
    assigned_admin_id?: number;
    resolution_note?: string;
    created_at?: string;
    updated_at?: string;
    resolved_at?: string;
  }>;
};

type AdminBugReportsResponse = {
  total: number;
  items: Array<{
    id: number;
    reporter_user_id: number;
    reporter_user_email?: string;
    message: string;
    page_url?: string;
    language?: string;
    status: 'open' | 'resolved';
    created_at?: string;
  }>;
};

const fetchAdminOverview = async (): Promise<AdminOverviewResponse> => {
  const response = await api.get('/api/v1/admin/overview');
  return response.data;
};

const fetchAdminUsers = async (params: {
  search: string;
  userType: 'all' | 'client' | 'professional';
  status: 'all' | 'true' | 'false';
}): Promise<AdminUsersResponse> => {
  try {
    const response = await api.get('/api/v1/admin/users', {
      params: {
        search: params.search,
        user_type: params.userType,
        is_active: params.status,
        limit: 25,
        offset: 0,
      },
    });
    return response.data;
  } catch (error: any) {
    // Fallback за стари backend инстанси без /admin/users.
    if (error?.response?.status === 404) {
      const overviewResponse = await api.get('/api/v1/admin/overview');
      const recentUsers = overviewResponse.data?.recent_users || [];

      return {
        total: recentUsers.length,
        limit: recentUsers.length,
        offset: 0,
        items: recentUsers.map((user: any) => ({
          id: user.id,
          email: user.email,
          username: user.email,
          full_name: user.full_name,
          user_type: user.user_type,
          is_active: Boolean(user.is_active),
          is_superuser: false,
          created_at: user.created_at,
          can_manage: false,
        })),
      };
    }

    throw error;
  }
};

const fetchFilteredAdminAuditLogs = async (params: {
  action: string;
  adminUserId: string;
  period: 'all' | '24h' | '7d' | '30d';
}): Promise<AdminAuditLogsResponse> => {
  const now = new Date();
  let fromDate: string | undefined;

  if (params.period === '24h') {
    fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  } else if (params.period === '7d') {
    fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
  } else if (params.period === '30d') {
    fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
  }

  const response = await api.get('/api/v1/admin/audit-logs', {
    params: {
      limit: 100,
      offset: 0,
      action: params.action === 'all' ? '' : params.action,
      admin_user_id: params.adminUserId === 'all' ? undefined : Number(params.adminUserId),
      from_date: fromDate,
    },
  });
  return response.data;
};

const fetchAdminReports = async (params: {
  status: 'all' | 'open' | 'in_review' | 'resolved' | 'rejected';
  targetType: 'all' | 'task' | 'profile' | 'message';
  reporterUserId: string;
}): Promise<AdminReportsResponse> => {
  try {
    const response = await api.get('/api/v1/admin/reports', {
      params: {
        limit: 100,
        offset: 0,
        status: params.status === 'all' ? '' : params.status,
        target_type: params.targetType === 'all' ? '' : params.targetType,
        reporter_user_id: params.reporterUserId === 'all' ? undefined : Number(params.reporterUserId),
      },
    });

    return response.data;
  } catch (error: any) {
    // Fallback за backend, който все още няма /admin/reports.
    if (error?.response?.status === 404) {
      return {
        total: 0,
        limit: 100,
        offset: 0,
        endpoint_unavailable: true,
        items: [],
      };
    }

    throw error;
  }
};

const fetchAdminBugReports = async (
  status: 'all' | 'open' | 'resolved'
): Promise<AdminBugReportsResponse> => {
  const response = await api.get('/api/v1/bug-reports/admin', {
    params: { status },
  });
  return response.data;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const StatCard: React.FC<{ title: string; value: number }> = ({ title, value }) => (
  <Paper sx={{ p: 2.5, height: '100%', borderRadius: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
      {value}
    </Typography>
  </Paper>
);

const AdminOverview: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const translateReportStatus = (status: 'open' | 'in_review' | 'resolved' | 'rejected') =>
    String(t(`admin.reports.status.${status}`));
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'client' | 'professional'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'true' | 'false'>('all');
  const [auditActionFilter, setAuditActionFilter] = useState<'all' | 'user_status_changed' | 'report_status_changed'>('all');
  const [auditPeriodFilter, setAuditPeriodFilter] = useState<'all' | '24h' | '7d' | '30d'>('7d');
  const [auditAdminFilter, setAuditAdminFilter] = useState<string>('all');
  const [reportStatusFilter, setReportStatusFilter] = useState<'all' | 'open' | 'in_review' | 'resolved' | 'rejected'>('open');
  const [reportTargetFilter, setReportTargetFilter] = useState<'all' | 'task' | 'profile' | 'message'>('all');
  const [reporterFilter, setReporterFilter] = useState<string>('all');
  const [bugStatusFilter, setBugStatusFilter] = useState<'all' | 'open' | 'resolved'>('open');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminOverview'],
    queryFn: fetchAdminOverview,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const usersQueryKey = useMemo(
    () => ['adminUsers', search, userTypeFilter, statusFilter],
    [search, userTypeFilter, statusFilter]
  );

  const {
    data: usersData,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery({
    queryKey: usersQueryKey,
    queryFn: () =>
      fetchAdminUsers({
        search,
        userType: userTypeFilter,
        status: statusFilter,
      }),
    retry: false,
    refetchOnWindowFocus: true,
  });

  const toggleUserActiveMutation = useMutation({
    mutationFn: async (input: { userId: number; isActive: boolean }) =>
      api.patch(`/api/v1/admin/users/${input.userId}/status`, { is_active: input.isActive }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adminOverview'] });
      await queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['adminAuditLogs'] });
      await queryClient.invalidateQueries({ queryKey: ['adminReports'] });
    },
  });

  const {
    data: auditLogsData,
    isLoading: auditLogsLoading,
    isError: auditLogsError,
  } = useQuery({
    queryKey: ['adminAuditLogs', auditActionFilter, auditPeriodFilter, auditAdminFilter],
    queryFn: () =>
      fetchFilteredAdminAuditLogs({
        action: auditActionFilter,
        adminUserId: auditAdminFilter,
        period: auditPeriodFilter,
      }),
    refetchOnWindowFocus: true,
  });

  const {
    data: reportsData,
    isLoading: reportsLoading,
    isError: reportsError,
  } = useQuery({
    queryKey: ['adminReports', reportStatusFilter, reportTargetFilter, reporterFilter],
    queryFn: () =>
      fetchAdminReports({
        status: reportStatusFilter,
        targetType: reportTargetFilter,
        reporterUserId: reporterFilter,
      }),
    retry: false,
    refetchOnWindowFocus: true,
  });

  const {
    data: bugReportsData,
    isLoading: bugReportsLoading,
    isError: bugReportsError,
  } = useQuery({
    queryKey: ['adminBugReports', bugStatusFilter],
    queryFn: () => fetchAdminBugReports(bugStatusFilter),
    retry: false,
    refetchOnWindowFocus: true,
  });

  const updateReportStatusMutation = useMutation({
    mutationFn: async (input: {
      reportId: number;
      status: 'open' | 'in_review' | 'resolved' | 'rejected';
      resolutionNote?: string;
    }) =>
      api.patch(`/api/v1/admin/reports/${input.reportId}/status`, {
        status: input.status,
        resolution_note: input.resolutionNote,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adminReports'] });
      await queryClient.invalidateQueries({ queryKey: ['adminAuditLogs'] });
    },
  });

  const updateBugReportStatusMutation = useMutation({
    mutationFn: async (input: { reportId: number; status: 'open' | 'resolved' }) =>
      api.patch(`/api/v1/bug-reports/admin/${input.reportId}`, { status: input.status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adminBugReports'] });
    },
  });

  const uniqueAdminOptions = useMemo(() => {
    const map = new Map<string, string>();
    (auditLogsData?.items || []).forEach((logItem) => {
      if (typeof logItem.admin_user_id === 'number') {
        map.set(String(logItem.admin_user_id), logItem.admin_user_email || `Admin #${logItem.admin_user_id}`);
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [auditLogsData]);

  const uniqueReporterOptions = useMemo(() => {
    const map = new Map<string, string>();
    (reportsData?.items || []).forEach((reportItem) => {
      if (typeof reportItem.reporter_user_id === 'number') {
        map.set(
          String(reportItem.reporter_user_id),
          reportItem.reporter_user_email || `User #${reportItem.reporter_user_id}`
        );
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [reportsData]);

  const handleExportAuditCsv = () => {
    const rows = auditLogsData?.items || [];
    const headers = ['id', 'created_at', 'admin_user_id', 'admin_user_email', 'action', 'entity_type', 'entity_id', 'target_user_id', 'details'];
    const csvLines = [
      headers.join(','),
      ...rows.map((item) =>
        [
          item.id,
          item.created_at || '',
          item.admin_user_id ?? '',
          `"${(item.admin_user_email || '').replace(/"/g, '""')}"`,
          `"${(item.action || '').replace(/"/g, '""')}"`,
          `"${(item.entity_type || '').replace(/"/g, '""')}"`,
          item.entity_id ?? '',
          item.target_user_id ?? '',
          `"${(item.details || '').replace(/"/g, '""')}"`,
        ].join(',')
      ),
    ];
    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `admin-audit-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSetReportStatus = (
    reportId: number,
    status: 'open' | 'in_review' | 'resolved' | 'rejected'
  ) => {
    const resolutionNote =
      status === 'resolved' || status === 'rejected'
        ? window.prompt(t('admin.reports.resolution_prompt')) || undefined
        : undefined;

    updateReportStatusMutation.mutate({
      reportId,
      status,
      resolutionNote,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageBackButton />
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        {t('admin.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.subtitle')}
      </Typography>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('admin.errors.overview_load')}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {isLoading
          ? Array.from({ length: 8 }).map((_, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" height={40} />
                </Paper>
              </Grid>
            ))
          : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.users_total')} value={data?.users.total || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.users_new_7d')} value={data?.users.new_7d || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.tasks_total')} value={data?.tasks.total || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.tasks_new_7d')} value={data?.tasks.new_7d || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.bids_total')} value={data?.bids.total || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.bids_pending')} value={data?.bids.pending || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.messages_unread')} value={data?.messages.unread_total || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title={t('admin.kpi.messages_new_7d')} value={data?.messages.new_7d || 0} />
              </Grid>
            </>
          )}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">{t('admin.recent_users.title')}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>{t('admin.users.type_label')}</TableCell>
                    <TableCell>{t('admin.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recent_users || []).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.user_type}</TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {!isLoading && (data?.recent_users || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>{t('common.no_data')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">{t('admin.recent_tasks.title')}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t('admin.recent_tasks.title_col')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell>{t('admin.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recent_tasks || []).map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>{task.id}</TableCell>
                      <TableCell>{task.title}</TableCell>
                      <TableCell>{task.status}</TableCell>
                      <TableCell>{formatDate(task.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {!isLoading && (data?.recent_tasks || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>{t('common.no_data')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">{t('admin.users.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('admin.users.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('admin.users.search_label')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('admin.users.type_label')}
                value={userTypeFilter}
                onChange={(event) => setUserTypeFilter(event.target.value as 'all' | 'client' | 'professional')}
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="client">{t('common.client')}</MenuItem>
                <MenuItem value="professional">{t('common.professional')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as 'all' | 'true' | 'false')}
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="true">{t('admin.users.active')}</MenuItem>
                <MenuItem value="false">{t('admin.users.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {usersError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('admin.errors.users_load')}
            </Alert>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('admin.users.type_label')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('admin.users.role')}</TableCell>
                  <TableCell>{t('admin.date')}</TableCell>
                  <TableCell align="right">{t('admin.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersLoading
                  ? Array.from({ length: 6 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell colSpan={8}>
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ))
                  : (usersData?.items || []).map((user) => {
                      const isUpdating =
                        toggleUserActiveMutation.isPending &&
                        toggleUserActiveMutation.variables?.userId === user.id;
                      const canManage = user.can_manage !== false;

                      return (
                        <TableRow key={user.id}>
                          <TableCell>{user.id}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.full_name || user.username || '-'}</TableCell>
                          <TableCell>{user.user_type}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={user.is_active ? 'success' : 'default'}
                              label={user.is_active ? t('admin.users.active') : t('admin.users.inactive')}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={user.is_superuser ? 'warning' : 'default'}
                              label={user.is_superuser ? t('admin.users.superuser') : t('admin.users.user')}
                            />
                          </TableCell>
                          <TableCell>{formatDate(user.created_at)}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isUpdating || !canManage}
                              onClick={() =>
                                toggleUserActiveMutation.mutate({
                                  userId: user.id,
                                  isActive: !user.is_active,
                                })
                              }
                            >
                              {canManage
                                ? (user.is_active ? t('admin.users.deactivate') : t('admin.users.activate'))
                                : t('admin.users.unavailable')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                {!usersLoading && (usersData?.items || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>{t('admin.users.empty')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">{t('admin.reports.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('admin.reports.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('common.status')}
                value={reportStatusFilter}
                onChange={(event) =>
                  setReportStatusFilter(
                    event.target.value as 'all' | 'open' | 'in_review' | 'resolved' | 'rejected'
                  )
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="open">{translateReportStatus('open')}</MenuItem>
                <MenuItem value="in_review">{translateReportStatus('in_review')}</MenuItem>
                <MenuItem value="resolved">{translateReportStatus('resolved')}</MenuItem>
                <MenuItem value="rejected">{translateReportStatus('rejected')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('admin.reports.target')}
                value={reportTargetFilter}
                onChange={(event) =>
                  setReportTargetFilter(event.target.value as 'all' | 'task' | 'profile' | 'message')
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="task">{t('admin.reports.target_types.task')}</MenuItem>
                <MenuItem value="profile">{t('admin.reports.target_types.profile')}</MenuItem>
                <MenuItem value="message">{t('admin.reports.target_types.message')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('admin.reports.reporter')}
                value={reporterFilter}
                onChange={(event) => setReporterFilter(event.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                {uniqueReporterOptions.map((option) => (
                  <MenuItem value={option.id} key={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {reportsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('admin.errors.reports_load')}
            </Alert>
          )}
          {reportsData?.endpoint_unavailable && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('admin.errors.reports_endpoint_missing')}
            </Alert>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>{t('admin.date')}</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>{t('admin.reports.reason')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('admin.reports.resolution')}</TableCell>
                  <TableCell align="right">{t('admin.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportsLoading
                  ? Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell colSpan={8}>
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ))
                  : (reportsData?.items || []).map((reportItem) => {
                      const isUpdating =
                        updateReportStatusMutation.isPending &&
                        updateReportStatusMutation.variables?.reportId === reportItem.id;

                      return (
                        <TableRow key={reportItem.id}>
                          <TableCell>{reportItem.id}</TableCell>
                          <TableCell>{formatDate(reportItem.created_at)}</TableCell>
                          <TableCell>
                            {reportItem.reporter_user_email || `#${reportItem.reporter_user_id || '-'}`}
                          </TableCell>
                          <TableCell>
                            {t(`admin.reports.target_types.${reportItem.target_type}`)} #{reportItem.target_id}
                          </TableCell>
                          <TableCell>
                            {reportItem.reason}
                            {reportItem.description ? ` / ${reportItem.description}` : ''}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={translateReportStatus(reportItem.status)} />
                          </TableCell>
                          <TableCell>{reportItem.resolution_note || '-'}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isUpdating}
                                onClick={() => {
                                  if (reportItem.status === 'open') {
                                    handleSetReportStatus(reportItem.id, 'in_review');
                                  } else if (reportItem.status === 'in_review') {
                                    handleSetReportStatus(reportItem.id, 'resolved');
                                  } else {
                                    handleSetReportStatus(reportItem.id, 'open');
                                  }
                                }}
                              >
                                {reportItem.status === 'open'
                                  ? t('admin.reports.start_review')
                                  : reportItem.status === 'in_review'
                                    ? t('admin.reports.resolve')
                                    : t('admin.reports.reopen')}
                              </Button>
                              {reportItem.status === 'in_review' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={isUpdating}
                                  onClick={() => handleSetReportStatus(reportItem.id, 'rejected')}
                                >
                                  {t('admin.reports.reject')}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                {!reportsLoading && (reportsData?.items || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>{t('admin.reports.empty')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">{t('admin.bugs.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('admin.bugs.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('common.status')}
                value={bugStatusFilter}
                onChange={(event) =>
                  setBugStatusFilter(event.target.value as 'all' | 'open' | 'resolved')
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="open">{t('admin.reports.status.open')}</MenuItem>
                <MenuItem value="resolved">{t('admin.reports.status.resolved')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {bugReportsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('admin.errors.bugs_load')}
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.date')}</TableCell>
                  <TableCell>{t('admin.reports.reporter')}</TableCell>
                  <TableCell>{t('bugs.message_label')}</TableCell>
                  <TableCell>{t('admin.bugs.page')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('admin.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bugReportsLoading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                )}
                {(bugReportsData?.items || []).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>{item.reporter_user_email || `#${item.reporter_user_id}`}</TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>{item.message}</TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.page_url || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          item.status === 'resolved'
                            ? t('admin.reports.status.resolved')
                            : t('admin.reports.status.open')
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {item.status === 'open' ? (
                        <Button
                          size="small"
                          onClick={() =>
                            updateBugReportStatusMutation.mutate({
                              reportId: item.id,
                              status: 'resolved',
                            })
                          }
                        >
                          {t('admin.reports.resolve')}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          onClick={() =>
                            updateBugReportStatusMutation.mutate({
                              reportId: item.id,
                              status: 'open',
                            })
                          }
                        >
                          {t('admin.reports.reopen')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!bugReportsLoading && (bugReportsData?.items || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>{t('admin.bugs.empty')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">{t('admin.audit.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('admin.audit.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('admin.audit.action')}
                value={auditActionFilter}
                onChange={(event) =>
                  setAuditActionFilter(event.target.value as 'all' | 'user_status_changed' | 'report_status_changed')
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="user_status_changed">user_status_changed</MenuItem>
                <MenuItem value="report_status_changed">report_status_changed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('admin.audit.period')}
                value={auditPeriodFilter}
                onChange={(event) => setAuditPeriodFilter(event.target.value as 'all' | '24h' | '7d' | '30d')}
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('admin.audit.period_all')}</MenuItem>
                <MenuItem value="24h">{t('admin.audit.period_24h')}</MenuItem>
                <MenuItem value="7d">{t('admin.audit.period_7d')}</MenuItem>
                <MenuItem value="30d">{t('admin.audit.period_30d')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={t('admin.audit.admin')}
                value={auditAdminFilter}
                onChange={(event) => setAuditAdminFilter(event.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                {uniqueAdminOptions.map((option) => (
                  <MenuItem value={option.id} key={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: '40px' }}
                onClick={handleExportAuditCsv}
                disabled={(auditLogsData?.items || []).length === 0}
              >
                {t('admin.audit.export_csv')}
              </Button>
            </Grid>
          </Grid>
          {auditLogsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('admin.errors.audit_load')}
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.date')}</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogsLoading
                  ? Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell colSpan={6}>
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ))
                  : (auditLogsData?.items || []).map((logItem) => (
                      <TableRow key={logItem.id}>
                        <TableCell>{formatDate(logItem.created_at)}</TableCell>
                        <TableCell>{logItem.admin_user_email || `#${logItem.admin_user_id || '-'}`}</TableCell>
                        <TableCell>{logItem.action}</TableCell>
                        <TableCell>
                          {logItem.entity_type}
                          {typeof logItem.entity_id === 'number' ? ` #${logItem.entity_id}` : ''}
                        </TableCell>
                        <TableCell>
                          {typeof logItem.target_user_id === 'number' ? `user #${logItem.target_user_id}` : '-'}
                        </TableCell>
                        <TableCell>{logItem.details || '-'}</TableCell>
                      </TableRow>
                    ))}
                {!auditLogsLoading && (auditLogsData?.items || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>{t('admin.audit.empty')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminOverview;
